// src/pages/portal/PortalInvoiceDetail.js
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { usePortalAuth } from '../../context/PortalAuthContext';
import PortalLayout from '../../components/PortalLayout';
import { deriveInvoiceFinancials } from '../../lib/jobFinancials';

function fmt(amount) {
  if (amount == null) return '—';
  return '$' + Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2 });
}
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}
function isOverdue(invoice) {
  if (!invoice?.due_date) return false;
  if (invoice.status?.toLowerCase() === 'paid') return false;
  return new Date(invoice.due_date) < new Date();
}

export default function PortalInvoiceDetail() {
  const { id } = useParams();
  const { clientRecord } = usePortalAuth();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();

  useEffect(() => {
    if (!clientRecord?.id) return;
    async function load() {
      const { data: inv } = await supabase
        .from('invoices')
        // Select only client-safe fields — never cost, markup, profit
        .select('id, title, invoice_number, amount, total, tax_amount, status, due_date, created_at, notes, payment_instructions')
        .eq('id', id)
        .eq('client_id', clientRecord.id)
        .single();
      if (!inv) { navigate('/portal/invoices'); return; }
      // Fetch applied payments for this invoice — balance derivation uses invoice_payments (FA-008)
      const { data: paymentsData } = await supabase
        .from('invoice_payments')
        .select('id, invoice_id, amount')
        .eq('invoice_id', id);
      setInvoice(inv);
      setPayments(paymentsData || []);
      setLoading(false);
    }
    load();
  }, [id, clientRecord]);

  function handlePrint() { window.print(); }

  if (loading) return <PortalLayout><div style={{ color: '#aaa', fontSize: '14px', paddingTop: '40px' }}>Loading…</div></PortalLayout>;
  if (!invoice) return null;

  const overdue = isOverdue(invoice);
  const displayStatus = overdue && invoice.status?.toLowerCase() !== 'paid' ? 'overdue' : invoice.status?.toLowerCase();
  const isPaid = invoice.status?.toLowerCase() === 'paid';
  // Use canonical helpers — never invoice.amount_paid (FA-008, deprecated, zero rows)
  const { invoiceTotal: total, appliedTotal: amountPaid, balance: remaining } = deriveInvoiceFinancials(invoice, payments);

  return (
    <PortalLayout>
      <style>{`@media print { header, footer, .no-print { display: none !important; } body { background: white !important; } }`}</style>

      {/* Breadcrumb */}
      <div style={{ marginBottom: '20px', fontSize: '13px', color: '#999' }} className="no-print">
        <Link to="/portal/invoices" style={{ color: '#c8a96e', textDecoration: 'none' }}>Invoices</Link>
        {' → '}
        <span>{invoice.title || `Invoice #${invoice.invoice_number || invoice.id?.slice(0,6)}`}</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: '24px', alignItems: 'start' }}>
        {/* Main invoice */}
        <div ref={printRef} className="print-area">
          <div style={{ background: '#fff', border: '1px solid #e8e4dc', borderRadius: '8px', padding: '36px' }}>
            {/* Invoice header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                  <div style={{ width: '32px', height: '32px', background: '#1a2e1a', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ color: '#c8a96e', fontWeight: '700', fontSize: '12px' }}>TL</span>
                  </div>
                  <span style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a18' }}>TLC Unlimited</span>
                </div>
                <div style={{ fontSize: '12px', color: '#aaa', lineHeight: '1.7' }}>
                  El Paso, TX<br />
                  john@tlcunlimitedtx.com
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '22px', fontWeight: '700', color: '#1a1a18', letterSpacing: '-0.02em' }}>INVOICE</div>
                <div style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>
                  #{invoice.invoice_number || invoice.id?.slice(0, 8).toUpperCase()}
                </div>
                <StatusPill status={displayStatus} />
              </div>
            </div>

            {/* Bill to / dates */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '28px', paddingBottom: '24px', borderBottom: '1px solid #f0ece4' }}>
              <div>
                <div style={{ fontSize: '10px', color: '#bbb', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: '600', marginBottom: '6px' }}>Bill To</div>
                <div style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a18' }}>{clientRecord?.name}</div>
                {clientRecord?.email && <div style={{ fontSize: '13px', color: '#888' }}>{clientRecord.email}</div>}
                {clientRecord?.address && <div style={{ fontSize: '13px', color: '#888' }}>{clientRecord.address}</div>}
              </div>
              <div style={{ textAlign: 'right' }}>
                <SummaryRow label="Issue Date" value={fmtDate(invoice.created_at)} />
                <SummaryRow label="Due Date" value={
                  <span style={{ color: overdue && !isPaid ? '#dc2626' : 'inherit' }}>{fmtDate(invoice.due_date)}</span>
                } />
              </div>
            </div>

            {/* Invoice title / description */}
            {invoice.title && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '15px', fontWeight: '600', color: '#1a1a18' }}>{invoice.title}</div>
              </div>
            )}

            {/* Total due — no line items table since no separate table exists */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 16px', background: '#f9fafb', borderRadius: '8px', alignItems: 'center' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Services Rendered</span>
                <span style={{ fontSize: '18px', fontWeight: '700', color: '#1a2e1a' }}>{fmt(total - (invoice.tax_amount || 0))}</span>
              </div>
              {invoice.tax_amount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 16px', fontSize: '13px', color: '#666' }}>
                  <span>Tax</span><span>{fmt(invoice.tax_amount)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 16px', borderTop: '2px solid #1a2e1a', marginTop: '8px', fontSize: '18px', fontWeight: '700', color: '#1a1a18' }}>
                <span>Total Due</span><span>{fmt(total)}</span>
              </div>
              {amountPaid > 0 && !isPaid && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 16px', fontSize: '14px', color: '#16a34a', fontWeight: '600' }}>
                  <span>Amount Paid</span><span>-{fmt(amountPaid)}</span>
                </div>
              )}
              {amountPaid > 0 && !isPaid && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 16px', background: '#fef3c7', borderRadius: '6px', marginTop: '4px', fontWeight: '700', color: '#92400e' }}>
                  <span>Remaining Balance</span><span>{fmt(remaining)}</span>
                </div>
              )}
            </div>

            {/* Notes */}
            {invoice.notes && (
              <div style={{ marginTop: '20px', padding: '16px', background: '#faf8f4', borderRadius: '6px', fontSize: '13px', color: '#555', lineHeight: '1.6' }}>
                <strong style={{ fontSize: '11px', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#aaa', display: 'block', marginBottom: '6px' }}>Notes</strong>
                {invoice.notes}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="no-print">
          {/* Payment section */}
          <div style={{ background: '#fff', border: '1px solid #e8e4dc', borderRadius: '8px', padding: '24px', marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a18', marginBottom: '16px' }}>Payment</div>
            {isPaid ? (
              <div style={{ background: '#e8f7ee', borderRadius: '7px', padding: '14px', fontSize: '13.5px', color: '#16a34a', fontWeight: '500', textAlign: 'center' }}>
                ✓ Paid in full
              </div>
            ) : (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: '#1a1a18', letterSpacing: '-0.02em' }}>{fmt(remaining)}</div>
                  <div style={{ fontSize: '12px', color: overdue ? '#dc2626' : '#888', marginTop: '2px' }}>
                    {overdue ? '⚠ Payment overdue' : `Due ${fmtDate(invoice.due_date)}`}
                  </div>
                </div>
                {/* Stripe placeholder */}
                <div style={{ background: '#f8f6f2', border: '1px dashed #d0cbc0', borderRadius: '8px', padding: '16px', textAlign: 'center', marginBottom: '12px' }}>
                  <div style={{ fontSize: '12px', color: '#aaa', marginBottom: '4px' }}>🔒 Online payment</div>
                  <div style={{ fontSize: '12px', color: '#aaa' }}>Coming soon via Stripe</div>
                </div>
                {invoice.payment_instructions ? (
                  <div style={{ fontSize: '12.5px', color: '#666', lineHeight: '1.6', padding: '12px', background: '#f8f6f2', borderRadius: '6px' }}>
                    <strong style={{ display: 'block', marginBottom: '4px', color: '#555' }}>How to pay:</strong>
                    {invoice.payment_instructions}
                  </div>
                ) : (
                  <div style={{ fontSize: '12.5px', color: '#888', lineHeight: '1.6' }}>
                    To pay, contact us at <strong>john@tlcunlimitedtx.com</strong> or call us directly.
                  </div>
                )}
              </>
            )}
          </div>

          {/* Print */}
          <div style={{ background: '#fff', border: '1px solid #e8e4dc', borderRadius: '8px', padding: '20px 24px', marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a18', marginBottom: '12px' }}>Download</div>
            <button onClick={handlePrint} style={{
              width: '100%', padding: '10px', background: '#f8f6f2',
              border: '1px solid #e0dbd0', borderRadius: '7px',
              fontSize: '13px', fontFamily: 'inherit', cursor: 'pointer',
              color: '#1a1a18', fontWeight: '500',
            }}>
              🖨 Print / Save as PDF
            </button>
            <div style={{ fontSize: '11px', color: '#bbb', textAlign: 'center', marginTop: '8px' }}>
              Use browser "Save as PDF" when printing
            </div>
          </div>

          {/* Summary */}
          <div style={{ background: '#fff', border: '1px solid #e8e4dc', borderRadius: '8px', padding: '20px 24px' }}>
            <SummaryRow label="Invoice #" value={invoice.invoice_number || invoice.id?.slice(0,8).toUpperCase()} />
            <SummaryRow label="Status" value={<StatusPill status={displayStatus} />} />
            <SummaryRow label="Issued" value={fmtDate(invoice.created_at)} />
            <SummaryRow label="Due" value={fmtDate(invoice.due_date)} />
            <SummaryRow label="Total" value={<strong>{fmt(total)}</strong>} />
            {amountPaid > 0 && <SummaryRow label="Paid" value={<span style={{ color: '#16a34a', fontWeight: '600' }}>{fmt(amountPaid)}</span>} />}
            {amountPaid > 0 && !isPaid && <SummaryRow label="Remaining" value={<strong style={{ color: '#d97706' }}>{fmt(remaining)}</strong>} />}
          </div>
        </div>
      </div>
    </PortalLayout>
  );
}

function StatusPill({ status }) {
  const map = {
    paid:    { bg: '#e8f7ee', color: '#16a34a', label: 'Paid' },
    unpaid:  { bg: '#fff8e1', color: '#d97706', label: 'Unpaid' },
    sent:    { bg: '#fff8e1', color: '#d97706', label: 'Due' },
    overdue: { bg: '#fdf0f0', color: '#dc2626', label: 'Overdue' },
    partial: { bg: '#e8f0ff', color: '#2563eb', label: 'Partial' },
    draft:   { bg: '#f0f0ec', color: '#666',    label: 'Pending' },
  };
  const s = map[status] || { bg: '#f0f0ec', color: '#666', label: status };
  return <span style={{ display: 'inline-block', background: s.bg, color: s.color, padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', marginTop: '6px' }}>{s.label}</span>;
}

function SummaryRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f6f3ee', fontSize: '12.5px', color: '#666' }}>
      <span>{label}</span><span>{value}</span>
    </div>
  );
}
